import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dates, formats, validators } from 'investira.sdk';
import {
  Stack,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Divider,
  Icon,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Box,
  useMediaQuery
} from 'investira.react.components';
import { HighchartsReact } from 'investira.react.charts';
import { PessoaImage } from '../../molecules';
import services from '../../../services';
import { calcularDiffNoPeriodo, criarHistorico } from '../ativoHistorico';
import AtivoRentabilidadeControls from './AtivoRentabilidadeControls';
import AtivoIndicesRentabilidade from '../AtivoIndicesRentabilidade';
import utils from '../../../utils';
import { getAtivoRentabilidadeDefaultIndicador } from './ativoRentabilidadeIndicadores';

const wSeriesDashStyles = ['Solid', 'ShortDash', 'ShortDot', 'ShortDashDot'];

/** Apresenta a rentabilidade dos ativos e carrega o benchmark selecionado. */
const AtivoRentabilidade = memo(pProps => {
  const {
    ativos = [],
    cotacoesPorId = {},
    defaultIndicadorId = 'SELIC',
    showIndices = true,
    headerHighlightEnabled = true,
    indiceProps = {
      highlightEnabled: false,
      subheaderEnabled: true
    }
  } = pProps;
  const chartRef = useRef(null);
  const [range, setRange] = useState(2);
  const xDefaultIndicadorId = getAtivoRentabilidadeDefaultIndicador(defaultIndicadorId);
  const [indicador, setIndicador] = useState(xDefaultIndicadorId);
  const [indicadorHistorico, setIndicadorHistorico] = useState({});
  const [isIndicadorLoading, setIsIndicadorLoading] = useState(false);
  const xAtivos = useMemo(() => ativos.filter(pAtivo => pAtivo?.ifeed_ativo_id), [ativos]);
  const xAtivoIds = useMemo(() => xAtivos.map(pAtivo => pAtivo.ifeed_ativo_id), [xAtivos]);
  const xAtivoIdsKey = xAtivoIds.join(',');
  const isUpSm = useMediaQuery(pTheme => pTheme.breakpoints.up('sm'));

  // Atualiza o benchmark quando a página passa a apresentar outro ativo.
  useEffect(() => {
    setIndicador(xDefaultIndicadorId);
  }, [xAtivoIdsKey, xDefaultIndicadorId]);

  /** Formata a rentabilidade atual de cada série em uma linha independente. */
  function pvGetCurrentRentabilidadeValues(pCotacoes = [], pRange = 'full') {
    return pCotacoes
      .map((xCotacao, xIndex) => {
        const xInfo = pvGetCurrentRentabilidadeInfo(xCotacao?.data?.[pRange]);
        if (validators.isNull(xInfo?.valor)) {
          return null;
        }

        const xAtivo = xCotacao?.raw
          ? utils.helpers.getAtivoDescricao(xCotacao.raw)
          : xCotacao.ativo;

        const xDescricao = isUpSm ? xAtivo : utils.typo.shortname(xAtivo, 24);

        return {
          id: `${xCotacao?.ativo}-${xIndex}`,
          nome: xDescricao,
          currentValue: `${xInfo.valor}%`,
          description: xInfo.data,
          dateTimestamp: xInfo.dateTimestamp,
          raw: xCotacao.raw
        };
      })
      .filter(xItem => {
        return !validators.isNull(xItem);
      });
  }

  function pvGetCurrentRentabilidadeInfo(pData = []) {
    const xCurrentPoint = [...(pData || [])].reverse().find(xPoint => {
      return !validators.isNull(xPoint?.rentabilidade);
    });

    if (validators.isEmpty(xCurrentPoint)) {
      return {
        valor: null,
        data: null,
        dateTimestamp: null
      };
    }

    return {
      valor: formats.formatNumber(xCurrentPoint.rentabilidade, 2, false),
      data: xCurrentPoint?.data ? formats.formatDate(xCurrentPoint.data) : null,
      dateTimestamp: xCurrentPoint?.data ? dates.toDate(xCurrentPoint.data).getTime() : null
    };
  }

  /** Formata a rentabilidade do período de cada ativo em uma linha independente. */
  function pvGetRentabilidadePeriodoValues(pCotacoes = [], pRange) {
    return pCotacoes
      .map((pCotacao, pIndex) => {
        const xValue = calcularDiffNoPeriodo(pCotacao?.data?.[pRange] || [], 'cotacao_ajustada');

        if (validators.isNull(xValue)) {
          return null;
        }

        const xFormattedValue = `${formats.formatNumber(xValue, 2, true, false)}%`;

        const xDescricao = utils.typo.shortname(
          (pCotacao?.raw && utils.helpers.getAtivoDescricao(pCotacao.raw)) || pCotacao.ativo,
          24
        );

        return {
          id: `${pCotacao?.ativo}-${pIndex}`,
          nome: xDescricao,
          currentValue: xFormattedValue,
          raw: pCotacao.raw
        };
      })
      .filter(pItem => !validators.isNull(pItem));
  }

  const xDados = useMemo(
    () =>
      xAtivos
        .map(xAtivo => {
          const xAtivoId = xAtivo.ifeed_ativo_id;
          const xCotacao = cotacoesPorId[xAtivoId];

          if (!xCotacao) {
            return null;
          }

          return {
            historico: criarHistorico([...(xCotacao.data || [])]),
            loading: Boolean(xCotacao.loading),
            error: Boolean(xCotacao.error),
            nome: xAtivo.ticker || xAtivo.ativo_seuid || xAtivo.ativo || String(xAtivoId),
            raw: xAtivo
          };
        })
        .filter(Boolean),
    [cotacoesPorId, xAtivos]
  );

  const xCotacoes = xDados
    .filter(xDado => !validators.isEmpty(xDado.historico?.full))
    .map(xDado => ({ ativo: xDado.nome, data: xDado.historico, raw: xDado.raw }));

  // Mantém na legenda os ativos finalizados sem histórico, sem criar pontos artificiais.
  const xSeriesAtivos = xDados
    .filter(xDado => !validators.isEmpty(xDado.historico?.full) || !xDado.loading)
    .map(xDado => ({ ativo: xDado.nome, data: xDado.historico, raw: xDado.raw }));

  const xCotacoesFinalizadas =
    xDados.length === xAtivoIds.length && xDados.every(xDado => !xDado.loading);

  const xDataInicio = xCotacoes.reduce((pDataInicio, pCotacao) => {
    const xData = pCotacao.data.full[0]?.data;
    return !pDataInicio || (xData && new Date(xData) < new Date(pDataInicio)) ? xData : pDataInicio;
  }, null);

  useEffect(() => {
    if (!xCotacoesFinalizadas || !xDataInicio || !indicador) {
      setIndicadorHistorico({});
      return undefined;
    }

    let xCurrent = true;
    setIsIndicadorLoading(true);
    services.indicadores.cotacao(
      { indicador_id: indicador, data_inicio: xDataInicio, cotacao_tipo_id: '1,4' },
      rRes => {
        if (!xCurrent) return;
        setIndicadorHistorico(criarHistorico(rRes.data || [], xDataInicio));
      },
      () => {
        if (!xCurrent) return;
        setIndicadorHistorico({});
      },
      () => {
        if (!xCurrent) return;
        setIsIndicadorLoading(false);
      }
    );

    return () => {
      xCurrent = false;
    };
  }, [indicador, xCotacoesFinalizadas, xDataInicio]);

  const pvFormatData = pData =>
    (pData || []).map(xPoint => [dates.toDate(xPoint.data).getTime(), xPoint.rentabilidade]);

  const pvCreateChartOptions = useCallback(
    (pCotacoes, pRange = 'full') => ({
      title: false,
      loading: {
        style: {
          backgroundColor: 'transparent',
          opacity: 1
        },
        labelStyle: {
          color: '#E0E0E3'
        }
      },
      chart: { zooming: { type: 'x' }, type: 'spline' },
      rangeSelector: {
        enabled: false,
        buttons: [
          { type: 'month', count: 1, text: '1M' },
          { type: 'month', count: 6, text: '6M' },
          { type: 'year', count: 1, text: '1A' },
          { type: 'year', count: 2, text: '2A' },
          { type: 'year', count: 3, text: '3A' },
          { type: 'year', count: 5, text: '5A' },
          { type: 'ytd', text: 'YTD' },
          { type: 'all', text: 'MÁX.' }
        ],
        selected: 2
      },
      xAxis: {
        type: 'datetime',
        crosshair: true,
        labels: { format: "{value:%b'%y}" }
      },
      yAxis: {
        type: 'linear',
        title: false,
        labels: {
          formatter() {
            return `${this.value}%`;
          }
        }
      },
      series: pCotacoes.map((xCotacao, xIndex) => {
        return {
          name: (xCotacao.raw && utils.helpers.getAtivoDescricao(xCotacao.raw)) || xCotacao.ativo,
          type: 'line',
          dashStyle: wSeriesDashStyles[xIndex % wSeriesDashStyles.length],
          data: pvFormatData(xCotacao.data?.[pRange])
        };
      }),
      tooltip: {
        crosshairs: true,
        shared: true,
        formatter() {
          const xDate = formats.formatDateCustom(this.point.x, 'DD/MMM/YYYY');
          let xContent = `<span style="font-size: 1em"><b>${xDate}</b></span><br/>`;
          this.points.forEach(xPoint => {
            xContent += `<span style="color:${xPoint.color}">●</span>${
              xPoint.series.name
            }: <b>${formats.formatNumber(xPoint.y, 2, false)}%</b><br/>`;
          });
          return xContent;
        }
      }
    }),
    []
  );
  const xRanges = [
    'umMes',
    'seisMeses',
    'umAno',
    'doisAnos',
    'tresAnos',
    'cincoAnos',
    'ytd',
    'full'
  ];
  const xSeries = indicadorHistorico.full
    ? [...xSeriesAtivos, { ativo: indicador, data: indicadorHistorico }]
    : xSeriesAtivos;
  const xCurrentRange = xRanges[range] || 'full';
  // Mantém o resumo no ponto mais recente do histórico completo, independentemente do gráfico.
  const xCurrentAtivoValues = pvGetCurrentRentabilidadeValues(xCotacoes);
  const xCurrentIndicadorValues = pvGetCurrentRentabilidadeValues(
    indicadorHistorico.full ? [{ ativo: indicador, data: indicadorHistorico }] : []
  );
  const xCurrentValues = [...xCurrentIndicadorValues, ...xCurrentAtivoValues];
  const xLatestCurrentValue = xCurrentValues.reduce((pLatestValue, pCurrentValue) => {
    if (
      validators.isNull(pLatestValue) ||
      pCurrentValue.dateTimestamp > pLatestValue.dateTimestamp
    ) {
      return pCurrentValue;
    }

    return pLatestValue;
  }, null);
  const xOptions = xSeries.length ? pvCreateChartOptions(xSeries, xCurrentRange) : null;
  const xHasCurrentRentabilidade =
    !validators.isEmpty(xCurrentAtivoValues) && !validators.isEmpty(xCurrentIndicadorValues);
  const xRentabilidadeUmAnoValues = pvGetRentabilidadePeriodoValues(xCotacoes, 'umAno');
  const xRentabilidadeInicioValues = pvGetRentabilidadePeriodoValues(xCotacoes, 'full');
  const xResumoRentabilidade = [
    ...(xHasCurrentRentabilidade
      ? [
          {
            id: 'comparada',
            label: xLatestCurrentValue.description,
            values: xCurrentValues
          }
        ]
      : [])
    // ...(!validators.isEmpty(xRentabilidadeUmAnoValues)
    //   ? [
    //       {
    //         id: 'umAno',
    //         label: 'Rentabilidade 12 meses',
    //         values: xRentabilidadeUmAnoValues,
    //         helpText: 'Considera agrupamentos, desdobramentos e proventos'
    //       }
    //     ]
    //   : []),
    // ...(!validators.isEmpty(xRentabilidadeInicioValues)
    //   ? [
    //       {
    //         id: 'inicio',
    //         label: 'Rentabilidade do início',
    //         values: xRentabilidadeInicioValues,
    //         helpText: 'Considera agrupamentos, desdobramentos e proventos'
    //       }
    //     ]
    //   : [])
  ];
  const xResumoRentabilidadeRows = xResumoRentabilidade.reduce((pRows, pItem) => {
    pItem.values.forEach(pValue => {
      const xRow = pRows.find(xItem => xItem.id === pValue.id);

      if (xRow) {
        xRow.metrics[pItem.id] = pValue;
        return;
      }

      pRows.push({
        id: pValue.id,
        nome: pValue.nome,
        raw: pValue.raw,
        metrics: { [pItem.id]: pValue }
      });
    });

    return pRows;
  }, []);

  if (!xOptions) return null;

  return (
    <Stack flexGrow={1}>
      <Card component="section">
        <CardHeader
          disableTypography
          sx={{
            '& .MuiCardHeader-content': {
              minWidth: 0
            }
          }}
          title={
            <Stack
              flexGrow={1}
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2}>
              <Stack flexGrow={1} spacing={1} sx={{ width: '100%' }}>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Stack width={21} flexShrink={0}>
                    <Icon iconName="chart_line" color="primary" size={21} />
                  </Stack>
                  <Stack direction="row" gap={1} alignItems="center">
                    <Typography
                      variant="subtitle1"
                      component="h3"
                      color="textPrimary"
                      sx={{ fontWeight: 600 }}
                      noWrap>
                      Rentabilidade Comparada
                    </Typography>
                    <Tooltip
                      title={
                        <Stack p={1}>Considera agrupamentos, desdobramentos e proventos</Stack>
                      }
                      arrow>
                      <span>
                        <Icon size={16} color="primary" iconName="information" />
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
                {headerHighlightEnabled && !validators.isEmpty(xResumoRentabilidade) ? (
                  <Stack spacing={2}>
                    <TableContainer sx={{ overflow: 'auto' }}>
                      <Table
                        size="small"
                        aria-label="Resumo da rentabilidade"
                        sx={{ width: '100%' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                position: 'sticky',
                                left: 0,
                                zIndex: 1,
                                width: '1%',
                                whiteSpace: 'nowrap',
                                backgroundColor: pTheme => pTheme.palette.secondary.dark
                              }}
                            />
                            {xResumoRentabilidade.map(pItem => (
                              <React.Fragment key={pItem.id}>
                                <TableCell align="right" sx={{ width: '1%', whiteSpace: 'nowrap' }}>
                                  {pItem.label ? (
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                      sx={{ fontWeight: 500 }}
                                      noWrap>
                                      {pItem.label}
                                    </Typography>
                                  ) : null}
                                </TableCell>
                                <TableCell aria-hidden sx={{ width: '1%', p: 0 }} />
                              </React.Fragment>
                            ))}
                            <TableCell aria-hidden sx={{ p: 0 }} />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {xResumoRentabilidadeRows.map(xRow => (
                            <TableRow key={xRow.id}>
                              <TableCell
                                component="th"
                                scope="row"
                                sx={{
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 1,
                                  width: '1%',
                                  whiteSpace: 'nowrap',
                                  backgroundColor: pTheme => pTheme.palette.secondary.dark
                                }}>
                                <Stack
                                  direction="row"
                                  gap={1}
                                  alignItems="center"
                                  sx={{ '& img': { display: 'block' } }}>
                                  {xRow?.raw?.logo_pessoa_id && (
                                    <PessoaImage
                                      logoPessoaId={xRow.raw.logo_pessoa_id}
                                      alt={xRow.raw.pessoa}
                                      hasLogo={xRow.raw.logo_ok}
                                      size={18}
                                    />
                                  )}
                                  <Typography
                                    data-testid="rentabilidade-resumo-nome"
                                    variant="body2"
                                    color="textPrimary"
                                    sx={{ fontWeight: 500 }}
                                    noWrap>
                                    {xRow.nome}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              {xResumoRentabilidade.map(pItem => {
                                const xValue = xRow.metrics[pItem.id];
                                const xIsOlderCurrentValue =
                                  pItem.id === 'comparada' &&
                                  xValue?.dateTimestamp < xLatestCurrentValue?.dateTimestamp;

                                return (
                                  <React.Fragment key={pItem.id}>
                                    <TableCell
                                      align="right"
                                      sx={{ width: '1%', whiteSpace: 'nowrap' }}>
                                      <Typography
                                        data-testid={
                                          xValue
                                            ? `rentabilidade-resumo-${pItem.id}-value`
                                            : undefined
                                        }
                                        variant="body2"
                                        color="textPrimary"
                                        sx={{ fontWeight: 500 }}
                                        noWrap>
                                        {xValue?.currentValue}
                                      </Typography>
                                    </TableCell>
                                    <TableCell
                                      aria-label={
                                        xIsOlderCurrentValue ? 'Data do valor' : undefined
                                      }
                                      sx={{ width: '1%', pl: 0, pr: 1, whiteSpace: 'nowrap' }}>
                                      {xIsOlderCurrentValue ? (
                                        <Tooltip
                                          title={`Valor referente à data: ${xValue.description}`}
                                          arrow>
                                          <Box sx={{ width: '16px', height: '16px' }}>
                                            <Icon
                                              size={16}
                                              color="primary"
                                              iconName="information"
                                            />
                                          </Box>
                                        </Tooltip>
                                      ) : null}
                                    </TableCell>
                                  </React.Fragment>
                                );
                              })}
                              <TableCell aria-hidden sx={{ p: 0 }} />
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Stack>
                ) : null}
              </Stack>
            </Stack>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          <Stack
            direction="row"
            alignItems="flex-end"
            spacing={2}
            p={headerHighlightEnabled ? 2 : 0}>
            <AtivoRentabilidadeControls
              range={{
                selected: range,
                buttons: xOptions.rangeSelector.buttons,
                setRange: (_pType, _pCount, pIndex) => setRange(pIndex)
              }}
              indicador={{ value: indicador, onChange: setIndicador }}
            />
          </Stack>
          <Stack sx={{ pt: 2 }}>
            <HighchartsReact
              key="cotacao"
              ref={chartRef}
              options={xOptions}
              isLoading={isIndicadorLoading || xDados.some(xDado => xDado.loading)}
            />
          </Stack>
          {showIndices && (
            <>
              <Divider sx={{ mx: 2 }} />
              <Stack>
                <AtivoIndicesRentabilidade
                  ativos={xAtivos}
                  cotacoesPorId={cotacoesPorId}
                  {...indiceProps}
                  sx={{ boxShadow: 'none', border: 'none' }}
                />
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
});

export default AtivoRentabilidade;
